import { NxtCrossSummary } from "@/features/auditoria/components/NxtCrossSummary";
import { AlvaraRegistrationPanel } from "@/features/auditoria/components/AlvaraRegistrationPanel";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  BotMessageSquare,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  FileSpreadsheet,
  GitCompare,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from "@/lib/api";
import { useAdminAuth } from "../context/AdminAuthContext";

const API_BASE = `${API_BASE_URL}/claro/api`;

interface TaxSpreadsheetSummary {
  totalRows: number;
  activeRows: number;
  baixadaRows: number;
  ignoredRows: number;
  uniqueCnpjs: number;
  invalidCnpjs: number;
  sheetName: string;
}

interface TaxSpreadsheetRecord {
  id: string;
  sourceType?: "consolidated" | "nxt-missing";
  originalName: string;
  storedName: string;
  uploadedAt: string;
  uploadedBy?: string;
  activatedAt?: string;
  sizeBytes: number;
  sha256: string;
  summary: TaxSpreadsheetSummary;
}

interface TaxSpreadsheetListResponse {
  activeId: string | null;
  files: TaxSpreadsheetRecord[];
}

interface DiffField {
  field: string;
  label: string;
  count: number;
}

interface DiffSampleField {
  field: string;
  label: string;
  from: string;
  to: string;
}

interface DiffUnitSample {
  cnpj: string;
  from?: Record<string, string>;
  to?: Record<string, string>;
  fields?: DiffSampleField[];
}

interface TaxSpreadsheetDiff {
  generatedAt: string;
  from: TaxSpreadsheetRecord | null;
  to: TaxSpreadsheetRecord | null;
  sameContent: boolean;
  summary: {
    added: number;
    removed: number;
    changed: number;
    unchanged: number;
    statusChanged: number;
    compared: number;
  };
  fieldChanges: DiffField[];
  statusTransitions: Array<{ transition: string; count: number }>;
  samples: {
    added: DiffUnitSample[];
    removed: DiffUnitSample[];
    changed: DiffUnitSample[];
    unchanged: DiffUnitSample[];
  };
}

interface NxtCrossResult {
  spreadsheet: TaxSpreadsheetRecord;
  generatedAt: string;
  summary: {
    total: number;
    found: number;
    missing: number;
    divergent: number;
    ambiguous: number;
  };
  samples: Array<{
    cnpj: string;
    classification: "found" | "missing" | "divergent" | "ambiguous";
    identification: string;
    pdUnits: Array<{ identification: string; name: string; regional: string }>;
    divergentFields?: string[];
  }>;
}

interface ProviderConfigStatus {
  configured: boolean;
  source: "dashboard" | "environment" | null;
  updatedAt: string | null;
}

interface AuditAssistantConfigStatus {
  groq: ProviderConfigStatus;
  openAi: ProviderConfigStatus;
  visionProvider: "openai" | "groq" | null;
}

interface ManualSyncStatus {
  complete: Array<{
    cnpj: string;
    identification: string;
    name: string;
    regional: string;
  }>;
  incomplete: Array<{
    cnpj: string;
    identification: string;
    name: string;
    regional: string;
  }>;
  unsynced: Array<{
    cnpj: string;
    identification: string;
    name: string;
    regional: string;
  }>;
  counts: { complete: number; incomplete: number; unsynced: number };
}

async function apiJson<T>(
  path: string,
  init?: RequestInit,
  token?: string | null,
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const json = await response.json().catch(() => null);
  if (!response.ok || json?.status === "error") {
    if (response.status === 401) {
      sessionStorage.removeItem("admin-user");
    }
    throw new Error(json?.message || `HTTP ${response.status}`);
  }
  return (json.result ?? json) as T;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Erro ao ler o arquivo."));
    reader.onload = () => {
      const value = String(reader.result ?? "");
      resolve(value.includes(",") ? value.split(",")[1] : value);
    };
    reader.readAsDataURL(file);
  });
}

function formatDate(value?: string): string {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR");
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatCnpj(value: string): string {
  return value.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
}

function getPreviousFile(
  files: TaxSpreadsheetRecord[],
  toId: string | null,
): TaxSpreadsheetRecord | null {
  const toFile = files.find((file) => file.id === toId);
  const sourceType = toFile?.sourceType ?? "consolidated";
  // So compara versoes da mesma fonte; misturar consolidado com nxt-missing produz um diff sem sentido.
  const sameSource = files.filter(
    (file) => (file.sourceType ?? "consolidated") === sourceType,
  );
  const sorted = [...sameSource].sort(
    (a, b) =>
      new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime(),
  );
  const index = sorted.findIndex((file) => file.id === toId);
  return index > 0 ? sorted[index - 1] : null;
}

function formatDateForFilename(date = new Date()): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}${mm}${dd}-${hh}${min}`;
}

function SummaryMetric({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums">
        {typeof value === "number" ? value.toLocaleString("pt-BR") : value}
      </div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function FileSummary({ file }: { file: TaxSpreadsheetRecord }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground lg:grid-cols-6">
      <span>{file.summary.totalRows.toLocaleString("pt-BR")} linhas</span>
      <span>{file.summary.activeRows.toLocaleString("pt-BR")} ativas</span>
      <span>{file.summary.baixadaRows.toLocaleString("pt-BR")} baixadas</span>
      <span>{file.summary.uniqueCnpjs.toLocaleString("pt-BR")} CNPJs</span>
      <span>{formatBytes(file.sizeBytes)}</span>
      <span>Abas {file.summary.sheetName}</span>
    </div>
  );
}

function DiffReport({ diff }: { diff: TaxSpreadsheetDiff | undefined }) {
  if (!diff) {
    return (
      <Card>
        <CardContent className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          Selecione duas planilhas para comparar.
        </CardContent>
      </Card>
    );
  }

  const sameContent =
    diff.sameContent ||
    (!!diff.from && !!diff.to && diff.from.sha256 === diff.to.sha256);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/30 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">Comparando versões</div>
          <Badge variant={sameContent ? "regular" : "secondary"}>
            {sameContent ? "Mesmo conteúdo" : "Conteúdo diferente"}
          </Badge>
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center">
          <div className="min-w-0 rounded-md border bg-background px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">De</div>
            <div className="truncate text-sm font-medium">
              {diff.from?.originalName ?? "Sem versão anterior"}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {diff.from
                ? `${formatDate(diff.from.uploadedAt)} | SHA ${diff.from.sha256.slice(0, 12)}`
                : "Primeira versão registrada"}
            </div>
          </div>
          <ArrowRight className="hidden h-5 w-5 text-muted-foreground lg:block" />
          <div className="min-w-0 rounded-md border bg-background px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">
              Para
            </div>
            <div className="truncate text-sm font-medium">
              {diff.to?.originalName ?? "Nenhuma versão selecionada"}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {diff.to
                ? `${formatDate(diff.to.uploadedAt)} | SHA ${diff.to.sha256.slice(0, 12)}`
                : "Selecione uma planilha"}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryMetric label="Adicionados" value={diff.summary.added} />
        <SummaryMetric label="Removidos" value={diff.summary.removed} />
        <SummaryMetric label="Alterados" value={diff.summary.changed} />
        <SummaryMetric
          label="Status alterado"
          value={diff.summary.statusChanged}
        />
        <SummaryMetric label="Sem mudança" value={diff.summary.unchanged} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Campos com diferença</CardTitle>
            <CardDescription>
              Contagem de CNPJs com alteração por campo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {diff.fieldChanges.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Nenhum campo alterado.
              </div>
            ) : (
              <div className="space-y-2">
                {diff.fieldChanges.slice(0, 10).map((field) => (
                  <div
                    key={field.field}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  >
                    <span>{field.label}</span>
                    <Badge variant="secondary">
                      {field.count.toLocaleString("pt-BR")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Transições de status</CardTitle>
            <CardDescription>Mudanças entre Ativa e Baixada</CardDescription>
          </CardHeader>
          <CardContent>
            {diff.statusTransitions.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Nenhuma transição de status.
              </div>
            ) : (
              <div className="space-y-2">
                {diff.statusTransitions.map((item) => (
                  <div
                    key={item.transition}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  >
                    <span>{item.transition}</span>
                    <Badge variant="secondary">
                      {item.count.toLocaleString("pt-BR")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Amostra de CNPJs alterados
          </CardTitle>
          <CardDescription>
            Primeiras linhas com diferenças detectadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {diff.samples.changed.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Nenhuma alteração de campo na amostra.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">CNPJ</th>
                    <th className="px-3 py-2">Campos</th>
                    <th className="px-3 py-2">Identificação</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.samples.changed.slice(0, 12).map((sample) => (
                    <tr key={sample.cnpj} className="border-b">
                      <td className="px-3 py-2 font-mono text-xs">
                        {formatCnpj(sample.cnpj)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(sample.fields ?? []).slice(0, 5).map((field) => (
                            <Badge
                              key={`${sample.cnpj}-${field.field}`}
                              variant="outline"
                            >
                              {field.label}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {sample.to?.identificacao ||
                          sample.from?.identificacao ||
                          "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminTaxSpreadsheetPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceType, setSourceType] = useState<"consolidated" | "nxt-missing">(
    "consolidated",
  );
  const [nxtSpreadsheetId, setNxtSpreadsheetId] = useState("");
  const uploadInput = useRef<HTMLInputElement>(null);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [isExportingXlsx, setIsExportingXlsx] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [groqApiKey, setGroqApiKey] = useState("");
  const [openAiApiKey, setOpenAiApiKey] = useState("");
  const [manualCnpjs, setManualCnpjs] = useState("");
  const [syncAllUnits, setSyncAllUnits] = useState(false);
  const [syncOnlyMissingBrasilApi, setSyncOnlyMissingBrasilApi] =
    useState(false);
  const [includeBrasilApi, setIncludeBrasilApi] = useState(true);
  const [includeOcr, setIncludeOcr] = useState(false);
  const { toast } = useToast();
  const { user, token, logout } = useAdminAuth();
  const queryClient = useQueryClient();

  const historyQuery = useQuery({
    queryKey: ["tax-spreadsheets"],
    queryFn: () =>
      apiJson<TaxSpreadsheetListResponse>(
        "/audit/spreadsheets",
        undefined,
        token,
      ),
  });

  const groqConfigQuery = useQuery({
    queryKey: ["audit-assistant-config"],
    queryFn: () =>
      apiJson<AuditAssistantConfigStatus>(
        "/audit/assistant/config",
        undefined,
        token,
      ),
    enabled: Boolean(token),
  });

  const manualSyncStatusQuery = useQuery({
    queryKey: ["audit-manual-sync-status"],
    queryFn: () =>
      apiJson<ManualSyncStatus>(
        "/audit/deep/manual-sync-status",
        undefined,
        token,
      ),
    enabled: Boolean(token && assistantOpen),
  });

  const files = historyQuery.data?.files ?? [];
  const activeId = historyQuery.data?.activeId ?? null;
  const activeFile = files.find((file) => file.id === activeId) ?? null;
  const nxtFiles = files.filter((file) => file.sourceType === "nxt-missing");
  const selectedNxtId = nxtSpreadsheetId || nxtFiles[0]?.id || "";

  const diffQuery = useQuery({
    queryKey: ["tax-spreadsheets-diff", fromId, toId, activeId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (fromId) params.set("from", fromId);
      if (toId) params.set("to", toId);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      return apiJson<TaxSpreadsheetDiff>(
        `/audit/spreadsheets/diff${suffix}`,
        undefined,
        token,
      );
    },
    enabled: files.length > 0,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile)
        throw new Error("Selecione uma planilha antes de enviar.");
      if (!token)
        throw new Error(
          "Sessao administrativa expirada. Entre novamente para enviar planilhas.",
        );
      if (!/\.(xlsx|xlsm)$/i.test(selectedFile.name)) throw new Error("Envie um arquivo .xlsx ou .xlsm.");
      if (!selectedFile.size || selectedFile.size > 15 * 1024 * 1024) throw new Error("O arquivo deve ter conteúdo e no máximo 15 MB.");
      const contentBase64 = await readFileAsBase64(selectedFile);
      return apiJson<{
        record: TaxSpreadsheetRecord;
        activeId: string | null;
        diff: TaxSpreadsheetDiff;
      }>(
        "/audit/spreadsheets/upload",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: selectedFile.name,
            contentBase64,
            uploadedBy: user?.email,
            sourceType,
            activate: sourceType === "consolidated",
          }),
        },
        token,
      );
    },
    onSuccess: async (result) => {
      toast({
        title: "Planilha carregada",
        description:
          result.record.sourceType === "consolidated"
            ? "A nova planilha foi ativada para a auditoria."
            : "Base NXT carregada. As duas contagens serão recalculadas automaticamente.",
      });
      setSelectedFile(null);
      if (uploadInput.current) uploadInput.current.value = "";
      if (result.record.sourceType === "nxt-missing") {
        setNxtSpreadsheetId(result.record.id);
      } else {
        setToId(result.record.id);
      }
      await queryClient.invalidateQueries({ queryKey: ["tax-spreadsheets"] });
      await queryClient.invalidateQueries({
        queryKey: ["tax-spreadsheets-diff"],
      });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      if (
        error instanceof Error &&
        error.message.includes("Sessao administrativa")
      )
        logout();
      toast({
        title: "Erro ao carregar planilha",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => {
      if (!token)
        throw new Error(
          "Sessao administrativa expirada. Entre novamente para ativar planilhas.",
        );
      return apiJson<{
        activeId: string;
        record: TaxSpreadsheetRecord;
        diff: TaxSpreadsheetDiff;
      }>(
        `/audit/spreadsheets/${encodeURIComponent(id)}/activate`,
        { method: "POST" },
        token,
      );
    },
    onSuccess: async (result) => {
      toast({
        title: "Planilha ativada",
        description: `${result.record.originalName} agora alimenta a dashboard de auditoria.`,
      });
      setToId(result.record.id);
      await queryClient.invalidateQueries({ queryKey: ["tax-spreadsheets"] });
      await queryClient.invalidateQueries({
        queryKey: ["tax-spreadsheets-diff"],
      });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      if (
        error instanceof Error &&
        error.message.includes("Sessao administrativa")
      )
        logout();
      toast({
        title: "Erro ao ativar planilha",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    },
  });

  const saveGroqKeyMutation = useMutation({
    mutationFn: () => {
      if (!token)
        throw new Error(
          "Sessão administrativa expirada. Entre novamente para salvar a chave.",
        );
      return apiJson<AuditAssistantConfigStatus>(
        "/audit/assistant/config",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ apiKey: groqApiKey }),
        },
        token,
      );
    },
    onSuccess: async () => {
      setGroqApiKey("");
      toast({
        title: "Chave do assistente salva",
        description:
          "A chave foi armazenada no servidor e não será exibida novamente.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit-assistant-config"],
      });
    },
    onError: (error) =>
      toast({
        title: "Erro ao salvar chave",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      }),
  });

  const clearGroqKeyMutation = useMutation({
    mutationFn: () => {
      if (!token)
        throw new Error(
          "Sessão administrativa expirada. Entre novamente para remover a chave.",
        );
      return apiJson<AuditAssistantConfigStatus>(
        "/audit/assistant/config",
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ provider: "groq" }),
        },
        token,
      );
    },
    onSuccess: async () => {
      toast({
        title: "Chave removida",
        description: "A chave configurada pelo painel foi removida.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit-assistant-config"],
      });
    },
    onError: (error) =>
      toast({
        title: "Erro ao remover chave",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      }),
  });

  const saveOpenAiKeyMutation = useMutation({
    mutationFn: () => {
      if (!token)
        throw new Error(
          "Sessão administrativa expirada. Entre novamente para salvar a chave.",
        );
      return apiJson<AuditAssistantConfigStatus>(
        "/audit/assistant/config",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ provider: "openai", apiKey: openAiApiKey }),
        },
        token,
      );
    },
    onSuccess: async () => {
      setOpenAiApiKey("");
      toast({
        title: "Chave OpenAI salva",
        description:
          "A chave foi armazenada no servidor e não será exibida novamente.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit-assistant-config"],
      });
    },
    onError: (error) =>
      toast({
        title: "Erro ao salvar chave OpenAI",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      }),
  });

  const clearOpenAiKeyMutation = useMutation({
    mutationFn: () => {
      if (!token)
        throw new Error(
          "Sessão administrativa expirada. Entre novamente para remover a chave.",
        );
      return apiJson<AuditAssistantConfigStatus>(
        "/audit/assistant/config",
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ provider: "openai" }),
        },
        token,
      );
    },
    onSuccess: async () => {
      toast({
        title: "Chave OpenAI removida",
        description: "A chave configurada pelo painel foi removida.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit-assistant-config"],
      });
    },
    onError: (error) =>
      toast({
        title: "Erro ao remover chave OpenAI",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      }),
  });

  const manualSyncMutation = useMutation({
    mutationFn: () => {
      if (!token)
        throw new Error(
          "Sessão administrativa expirada. Entre novamente para sincronizar.",
        );
      if (syncOnlyMissingBrasilApi) {
        return apiJson(
          "/audit/deep/run",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ onlyMissingBrasilApi: true }),
          },
          token,
        );
      }
      if (syncAllUnits) {
        return apiJson(
          "/audit/deep/run",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              allUnits: true,
              includeBrasilApi,
              includeOcr,
            }),
          },
          token,
        );
      }
      const cnpjs = manualCnpjs
        .split(/[\s,;]+/)
        .map((value) => value.replace(/\D/g, ""))
        .filter(Boolean);
      return apiJson(
        "/audit/deep/manual-sync",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cnpjs, includeBrasilApi, includeOcr }),
        },
        token,
      );
    },
    onSuccess: async () => {
      toast({
        title: "Sincronização iniciada",
        description: syncAllUnits
          ? "O processamento em massa foi iniciado conforme as opções selecionadas."
          : "As unidades selecionadas foram adicionadas à fila de validação.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit-manual-sync-status"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit", "deep-coverage"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["audit", "deep-queue"],
      });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) =>
      toast({
        title: "Não foi possível sincronizar",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      }),
  });

  const sortedForSelect = useMemo(
    () =>
      files
        .filter(
          (file) => (file.sourceType ?? "consolidated") === "consolidated",
        )
        .sort(
          (a, b) =>
            new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
        ),
    [files],
  );

  const handleDownloadXlsxReport = async () => {
    try {
      setIsExportingXlsx(true);
      const workbook = XLSX.utils.book_new();
      const effectiveToId = toId || activeId || files[0]?.id || "";
      const effectiveFromId =
        fromId || getPreviousFile(files, effectiveToId)?.id || "";
      const params = new URLSearchParams();
      if (effectiveFromId) params.set("from", effectiveFromId);
      if (effectiveToId) params.set("to", effectiveToId);
      const suffix = params.toString() ? `?${params.toString()}` : "";

      // Always fetch the diff on export to avoid generating placeholder rows
      // when users click download before the comparison query finishes loading.
      const diff = await apiJson<TaxSpreadsheetDiff>(
          `/audit/spreadsheets/diff${suffix}`,
          undefined,
          token,
        );

      // Tabela 1: Historico de Arquivos
      const historyRows = files.map((file) => ({
        Arquivo: file.originalName,
        Ativa: file.id === activeId ? "Sim" : "Nao",
        EnviadoEm: formatDate(file.uploadedAt),
        EnviadoPor: file.uploadedBy || "-",
        AtivadaEm: formatDate(file.activatedAt),
        TotalLinhas: file.summary.totalRows,
        LinhasAtivas: file.summary.activeRows,
        LinhasBaixadas: file.summary.baixadaRows,
        CnpjsUnicos: file.summary.uniqueCnpjs,
        LinhasIgnoradas: file.summary.ignoredRows,
        CnpjsInvalidos: file.summary.invalidCnpjs,
        Abas: file.summary.sheetName,
        TamanhoBytes: file.sizeBytes,
        Sha256: file.sha256,
      }));

      const historySheet = XLSX.utils.json_to_sheet(
        historyRows.length > 0
          ? historyRows
          : [{ Info: "Nenhum arquivo no historico." }],
      );
      XLSX.utils.book_append_sheet(
        workbook,
        historySheet,
        "Historico de Arquivos",
      );

      // Tabela 2: Relatorio de Diferencas
      const diffRows: Array<Record<string, string | number>> = [];

      if (!diff) {
        diffRows.push({
          Secao: "Info",
          Item: "Mensagem",
          Valor: "Selecione duas planilhas para comparar antes de exportar.",
        });
      } else {
        diffRows.push(
          {
            Secao: "Resumo",
            Item: "Gerado em",
            Valor: formatDate(diff.generatedAt),
          },
          {
            Secao: "Resumo",
            Item: "Arquivo origem",
            Valor: diff.from?.originalName ?? "Sem versao anterior",
          },
          {
            Secao: "Resumo",
            Item: "Arquivo destino",
            Valor: diff.to?.originalName ?? "Sem versao selecionada",
          },
          {
            Secao: "Resumo",
            Item: "Mesmo conteudo",
            Valor: diff.sameContent ? "Sim" : "Nao",
          },
          { Secao: "Resumo", Item: "Adicionados", Valor: diff.summary.added },
          { Secao: "Resumo", Item: "Removidos", Valor: diff.summary.removed },
          { Secao: "Resumo", Item: "Alterados", Valor: diff.summary.changed },
          {
            Secao: "Resumo",
            Item: "Sem mudanca",
            Valor: diff.summary.unchanged,
          },
          {
            Secao: "Resumo",
            Item: "Status alterado",
            Valor: diff.summary.statusChanged,
          },
          { Secao: "Resumo", Item: "Comparados", Valor: diff.summary.compared },
        );

        if (diff.fieldChanges.length > 0) {
          for (const field of diff.fieldChanges) {
            diffRows.push({
              Secao: "Campos com diferenca",
              Item: field.label,
              Campo: field.field,
              Valor: field.count,
            });
          }
        } else {
          diffRows.push({
            Secao: "Campos com diferenca",
            Item: "Mensagem",
            Valor: "Nenhum campo alterado.",
          });
        }

        if (diff.statusTransitions.length > 0) {
          for (const transition of diff.statusTransitions) {
            diffRows.push({
              Secao: "Transicoes de status",
              Item: transition.transition,
              Valor: transition.count,
            });
          }
        } else {
          diffRows.push({
            Secao: "Transicoes de status",
            Item: "Mensagem",
            Valor: "Nenhuma transicao de status.",
          });
        }
      }

      const diffSheet = XLSX.utils.json_to_sheet(diffRows);
      XLSX.utils.book_append_sheet(
        workbook,
        diffSheet,
        "Relatorio de Diferencas",
      );

      // Tabela 3: Todas as unidades comparadas (completo, sem limite de amostra)
      const allUnitsRows: Array<Record<string, string>> = [];

      for (const sample of diff?.samples.changed ?? []) {
        allUnitsRows.push({
          CNPJ: formatCnpj(sample.cnpj),
          Status: "Alterado",
          IdentificacaoOrigem: sample.from?.identificacao ?? "-",
          IdentificacaoDestino: sample.to?.identificacao ?? "-",
          CamposAlterados:
            (sample.fields ?? []).map((f) => f.label).join(", ") || "-",
          Detalhes:
            (sample.fields ?? [])
              .map((f) => `${f.label}: "${f.from}" -> "${f.to}"`)
              .join(" | ") || "-",
        });
      }
      for (const sample of diff?.samples.added ?? []) {
        allUnitsRows.push({
          CNPJ: formatCnpj(sample.cnpj),
          Status: "Adicionado",
          IdentificacaoOrigem: "-",
          IdentificacaoDestino: sample.to?.identificacao ?? "-",
          CamposAlterados: "-",
          Detalhes: "-",
        });
      }
      for (const sample of diff?.samples.removed ?? []) {
        allUnitsRows.push({
          CNPJ: formatCnpj(sample.cnpj),
          Status: "Removido",
          IdentificacaoOrigem: sample.from?.identificacao ?? "-",
          IdentificacaoDestino: "-",
          CamposAlterados: "-",
          Detalhes: "-",
        });
      }
      for (const sample of diff?.samples.unchanged ?? []) {
        allUnitsRows.push({
          CNPJ: formatCnpj(sample.cnpj),
          Status: "Sem mudanca",
          IdentificacaoOrigem: sample.from?.identificacao ?? "-",
          IdentificacaoDestino: sample.to?.identificacao ?? "-",
          CamposAlterados: "-",
          Detalhes: "-",
        });
      }

      const allUnitsSheet = XLSX.utils.json_to_sheet(
        allUnitsRows.length > 0
          ? allUnitsRows
          : [{ Info: "Nenhuma unidade comparada disponivel." }],
      );
      XLSX.utils.book_append_sheet(
        workbook,
        allUnitsSheet,
        "Todas as Unidades Comparadas",
      );

      const filename = `relatorio-tributario-${formatDateForFilename()}.xlsx`;
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Relatorio XLSX gerado",
        description: `Download iniciado com ${allUnitsRows.length.toLocaleString("pt-BR")} unidades comparadas (adicionadas, alteradas, removidas e sem mudanca).`,
      });
    } catch (error) {
      toast({
        title: "Erro ao gerar XLSX",
        description:
          error instanceof Error
            ? error.message
            : "Nao foi possivel exportar o relatorio.",
        variant: "destructive",
      });
    } finally {
      setIsExportingXlsx(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Validação Tributária
          </h2>
          <p className="text-muted-foreground">
            Gerencie as planilhas usadas na auditoria cadastral.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadXlsxReport}
            disabled={
              historyQuery.isLoading || files.length === 0 || isExportingXlsx
            }
            className="gap-2"
          >
            {isExportingXlsx ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExportingXlsx ? "Gerando XLSX..." : "Baixar XLSX"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setAssistantOpen(true)}
            className="gap-2"
          >
            <BotMessageSquare className="h-4 w-4" />
            IA Assistant
          </Button>
          <Button
            variant="outline"
            onClick={() => historyQuery.refetch()}
            disabled={historyQuery.isFetching}
            className="gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${historyQuery.isFetching ? "animate-spin" : ""}`}
            />
            Atualizar
          </Button>
          <Button
            onClick={() => window.open("/auditoria", "_blank")}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Abrir Dashboard
          </Button>
        </div>
      </div>

      <AlvaraRegistrationPanel token={token} spreadsheetId={sourceType === 'nxt-missing' ? selectedNxtId || undefined : undefined} />
      <Dialog open={assistantOpen} onOpenChange={setAssistantOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>IA Assistant de Sincronização</DialogTitle>
            <DialogDescription>
              Configure os provedores de IA, execute sincronizações manuais e
              acompanhe a cobertura das unidades.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="openai" className="mt-2">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="openai">OpenAI OCR</TabsTrigger>
              <TabsTrigger value="groq">Chave Groq</TabsTrigger>
              <TabsTrigger value="sync">Sincronização manual</TabsTrigger>
              <TabsTrigger value="coverage">Cobertura</TabsTrigger>
            </TabsList>
            <TabsContent value="openai" className="space-y-4">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Chave de API OpenAI</span>
                <Input
                  type="password"
                  value={openAiApiKey}
                  onChange={(event) => setOpenAiApiKey(event.target.value)}
                  placeholder="sk-..."
                  autoComplete="new-password"
                />
                <span className="block text-xs text-muted-foreground">
                  {groqConfigQuery.data?.openAi.configured
                    ? `OpenAI configurada via ${groqConfigQuery.data.openAi.source === "dashboard" ? "painel administrativo" : "variável de ambiente"}. GPT-5.6 Luna será usado para ler Alvará e AVCB.`
                    : "Configure a OpenAI para usar GPT-5.6 Luna no OCR de Alvará e AVCB."}
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                Provedor de OCR ativo: {groqConfigQuery.data?.visionProvider === "openai" ? "OpenAI GPT-5.6 Luna" : groqConfigQuery.data?.visionProvider === "groq" ? "Groq (fallback)" : "nenhum configurado"}.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => saveOpenAiKeyMutation.mutate()}
                  disabled={!openAiApiKey.trim() || saveOpenAiKeyMutation.isPending}
                >
                  {saveOpenAiKeyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salvar chave
                </Button>
                <Button
                  variant="outline"
                  onClick={() => clearOpenAiKeyMutation.mutate()}
                  disabled={!groqConfigQuery.data?.openAi.configured || clearOpenAiKeyMutation.isPending}
                >
                  Remover
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="groq" className="space-y-4">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Chave de API Groq</span>
                <Input
                  type="password"
                  value={groqApiKey}
                  onChange={(event) => setGroqApiKey(event.target.value)}
                  placeholder="gsk_..."
                  autoComplete="new-password"
                />
                <span className="block text-xs text-muted-foreground">
                  {groqConfigQuery.data?.groq.configured
                    ? `Configurada via ${groqConfigQuery.data.groq.source === "dashboard" ? "painel administrativo" : "variável de ambiente"}. Usada pelo chat e como fallback do OCR.`
                    : "Nenhuma chave configurada."}
                </span>
              </label>
              <div className="flex gap-2">
                <Button
                  onClick={() => saveGroqKeyMutation.mutate()}
                  disabled={!groqApiKey.trim() || saveGroqKeyMutation.isPending}
                >
                  {saveGroqKeyMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Salvar chave
                </Button>
                <Button
                  variant="outline"
                  onClick={() => clearGroqKeyMutation.mutate()}
                  disabled={
                    !groqConfigQuery.data?.groq.configured ||
                    clearGroqKeyMutation.isPending
                  }
                >
                  Remover
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="sync" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                  <input
                    type="radio"
                    checked={!syncAllUnits && !syncOnlyMissingBrasilApi}
                    onChange={() => {
                      setSyncAllUnits(false);
                      setSyncOnlyMissingBrasilApi(false);
                    }}
                  />
                  Unidades selecionadas
                </label>
                <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                  <input
                    type="radio"
                    checked={syncAllUnits}
                    onChange={() => {
                      setSyncAllUnits(true);
                      setSyncOnlyMissingBrasilApi(false);
                    }}
                  />
                  Todas as unidades elegíveis
                </label>
                <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                  <input
                    type="radio"
                    checked={syncOnlyMissingBrasilApi}
                    onChange={() => {
                      setSyncAllUnits(false);
                      setSyncOnlyMissingBrasilApi(true);
                    }}
                  />
                  Somente pendentes da BrasilAPI
                </label>
              </div>
              {!syncAllUnits && !syncOnlyMissingBrasilApi && (
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium">
                    CNPJs das unidades
                  </span>
                  <textarea
                    className="min-h-28 w-full rounded-md border bg-background p-3 text-sm"
                    value={manualCnpjs}
                    onChange={(event) => setManualCnpjs(event.target.value)}
                    placeholder="Um ou mais CNPJs, separados por vírgula ou quebra de linha"
                  />
                </label>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                  <input
                    type="checkbox"
                    checked={includeBrasilApi}
                    onChange={(event) =>
                      setIncludeBrasilApi(event.target.checked)
                    }
                    disabled={syncOnlyMissingBrasilApi}
                  />
                  Consultar BrasilAPI
                </label>
                <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                  <input
                    type="checkbox"
                    checked={includeOcr}
                    onChange={(event) => setIncludeOcr(event.target.checked)}
                    disabled={syncOnlyMissingBrasilApi}
                  />
                  Ler Alvará e AVCB via OCR
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Somente pendentes consulta BrasilAPI apenas para CNPJs sem
                retorno válido e não reinicia o OCR. Sem opções externas, a
                sincronização atualiza apenas as bases PropertyDocs e
                tributária.
              </p>
              <Button
                onClick={() => manualSyncMutation.mutate()}
                disabled={
                  manualSyncMutation.isPending ||
                  (!syncAllUnits &&
                    !syncOnlyMissingBrasilApi &&
                    !manualCnpjs.replace(/\D/g, ""))
                }
              >
                {manualSyncMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {syncOnlyMissingBrasilApi
                  ? "Sincronizar pendências da BrasilAPI"
                  : "Iniciar sincronização"}
              </Button>
            </TabsContent>
            <TabsContent value="coverage" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <SummaryMetric
                  label="Completas"
                  value={manualSyncStatusQuery.data?.counts.complete ?? "—"}
                  hint="BrasilAPI e documentos processados"
                />
                <SummaryMetric
                  label="Incompletas"
                  value={manualSyncStatusQuery.data?.counts.incomplete ?? "—"}
                  hint="BrasilAPI concluída, OCR pendente"
                />
                <SummaryMetric
                  label="Não sincronizadas"
                  value={manualSyncStatusQuery.data?.counts.unsynced ?? "—"}
                  hint="BrasilAPI ainda não consultada"
                />
              </div>
              {(["complete", "incomplete", "unsynced"] as const).map(
                (group) => (
                  <div key={group}>
                    <h3 className="mb-2 text-sm font-semibold">
                      {group === "complete"
                        ? "Unidades completas"
                        : group === "incomplete"
                          ? "Unidades incompletas"
                          : "Unidades não sincronizadas"}
                    </h3>
                    <div className="max-h-40 overflow-y-auto rounded-md border">
                      {manualSyncStatusQuery.data?.[group]
                        .slice(0, 20)
                        .map((unit) => (
                          <div
                            key={unit.cnpj}
                            className="border-b px-3 py-2 text-xs last:border-0"
                          >
                            <p className="font-medium">{unit.name}</p>
                            <p className="text-muted-foreground">
                              {formatCnpj(unit.cnpj)} · {unit.regional}
                            </p>
                          </div>
                        )) ?? (
                        <p className="p-3 text-sm text-muted-foreground">
                          Carregando cobertura...
                        </p>
                      )}
                    </div>
                  </div>
                ),
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Upload className="h-5 w-5" />
              Carregar Excel
            </CardTitle>
            <CardDescription>
              Tributário: substitui o consolidado ativo. NXT: atualiza a conferência NXT sem substituir o tributário. Formatos .xlsx e .xlsm, até 15 MB.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              ref={uploadInput}
              aria-label="Planilha tributária ou NXT"
              disabled={uploadMutation.isPending}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
              onChange={(event) =>
                setSelectedFile(event.target.files?.[0] ?? null)
              }
            />
            <label className="space-y-1.5">
              <span className="text-sm font-medium">Tipo da fonte</span>
              <select
                disabled={uploadMutation.isPending}
                value={sourceType}
                onChange={(event) =>
                  setSourceType(
                    event.target.value as "consolidated" | "nxt-missing",
                  )
                }
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="consolidated">
                  Cadastro consolidado (Claro / NXT / Coligadas)
                </option>
                <option value="nxt-missing">
                  Base completa Claro NXT
                </option>
              </select>
            </label>
            <p className="text-xs text-muted-foreground">{sourceType === "consolidated" ? "Abas aceitas: Claro, Claro NXT e Coligadas. A aba Claro NXT também atualiza a contagem quando este consolidado é mais recente." : "Abas aceitas: Claro NXT, Geral e/ou TRIBUTARIO. Envie a base completa; não é necessário marcar manualmente os ausentes. A contagem usa a fonte mais recente entre esta base e o consolidado ativo."}</p>
            {selectedFile && (
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <div className="font-medium">{selectedFile.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatBytes(selectedFile.size)}
                </div>
              </div>
            )}
            <Button
              onClick={() => uploadMutation.mutate()}
              disabled={!selectedFile || uploadMutation.isPending}
              className="gap-2"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              {uploadMutation.isPending
                ? "Enviando..."
                : sourceType === "consolidated"
                  ? "Enviar e Ativar"
                  : "Enviar e conferir NXT"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              Planilha Ativa
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeFile ? (
              <div className="space-y-3">
                <div>
                  <div className="font-medium">{activeFile.originalName}</div>
                  <div className="text-xs text-muted-foreground">
                    Ativada em {formatDate(activeFile.activatedAt)}
                  </div>
                </div>
                <FileSummary file={activeFile} />
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Nenhuma planilha ativa encontrada.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {nxtFiles.length > 0 && (
        <Card id="nxt-comparison">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <GitCompare className="h-5 w-5" />
              Cruzamento Claro NXT x PropertyDocs
            </CardTitle>
            <CardDescription>
              Contagem por CNPJ único nos dois cenários: cadastro da unidade ou Alvará, e somente Alvará. A fonte efetivamente utilizada aparece junto ao resultado.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Fonte NXT
              </span>
              <select
                value={selectedNxtId}
                onChange={(event) => setNxtSpreadsheetId(event.target.value)}
                className="h-10 w-full max-w-xl rounded-md border bg-background px-3 text-sm"
              >
                {nxtFiles.map((file) => (
                  <option key={file.id} value={file.id}>
                    {formatDate(file.uploadedAt)} - {file.originalName}
                  </option>
                ))}
              </select>
            </label>
            <NxtCrossSummary spreadsheetId={selectedNxtId} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5" />
            Histórico de Arquivos
          </CardTitle>
          <CardDescription>
            Selecione qual planilha deve alimentar a dashboard de auditoria.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historyQuery.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : files.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
              Nenhum arquivo carregado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Arquivo</th>
                    <th className="px-3 py-2">Enviado em</th>
                    <th className="px-3 py-2">Resumo</th>
                    <th className="px-3 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => {
                    const isActive = file.id === activeId;
                    const isNxtSource = file.sourceType === "nxt-missing";
                    return (
                      <tr
                        key={file.id}
                        className="border-b align-top hover:bg-muted/30"
                      >
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {file.originalName}
                            </span>
                            {isActive && <Badge variant="regular">Ativa</Badge>}
                            {isNxtSource && (
                              <Badge variant="outline">NXT ausente</Badge>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            SHA {file.sha256.slice(0, 12)}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs text-muted-foreground">
                          <div>{formatDate(file.uploadedAt)}</div>
                          {file.uploadedBy && <div>{file.uploadedBy}</div>}
                        </td>
                        <td className="px-3 py-3">
                          <FileSummary file={file} />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (isNxtSource) {
                                  setNxtSpreadsheetId(file.id);
                                  document.getElementById("nxt-comparison")?.scrollIntoView({ behavior: "smooth" });
                                  return;
                                }
                                setFromId(
                                  getPreviousFile(files, file.id)?.id ?? "",
                                );
                                setToId(file.id);
                              }}
                            >
                              {isNxtSource ? "Conferir NXT" : "Comparar"}
                            </Button>
                            <Button
                              size="sm"
                              disabled={
                                isActive ||
                                isNxtSource ||
                                activateMutation.isPending
                              }
                              onClick={() => activateMutation.mutate(file.id)}
                            >
                              {isActive
                                ? "Em uso"
                                : isNxtSource
                                  ? "Somente histórico"
                                  : "Usar"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GitCompare className="h-5 w-5" />
            Relatório de Diferenças
          </CardTitle>
          <CardDescription>
            Compare duas versões carregadas para ver inclusões, remoções e
            alterações cadastrais.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-end">
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                De
              </span>
              <select
                value={fromId}
                onChange={(event) => setFromId(event.target.value)}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Anterior automática</option>
                {sortedForSelect.map((file) => (
                  <option key={file.id} value={file.id}>
                    {formatDate(file.uploadedAt)} - {file.originalName}
                  </option>
                ))}
              </select>
            </label>
            <ArrowRight className="hidden h-5 w-5 text-muted-foreground lg:block" />
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Para
              </span>
              <select
                value={toId}
                onChange={(event) => setToId(event.target.value)}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Planilha ativa</option>
                {sortedForSelect.map((file) => (
                  <option key={file.id} value={file.id}>
                    {formatDate(file.uploadedAt)} - {file.originalName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {diffQuery.isError ? (
            <div role="alert" className="space-y-2 text-sm text-destructive">
              <p>Não foi possível carregar o relatório de diferenças. {diffQuery.error instanceof Error ? diffQuery.error.message : ""}</p>
              <Button variant="outline" disabled={diffQuery.isFetching} onClick={() => diffQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : diffQuery.isFetching ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <DiffReport diff={diffQuery.data} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
